/* Helper Constants */

import { type MeterHistoryValue, type UsageData, type UsageDataDatasetEntry } from './meter-types'
import moment from 'moment'
import { groupBy } from '../../utils/utils'

const TEMPERATURE_USAGE_ENTRY: UsageDataDatasetEntry = {

  label: 'Lämpötila',
  backgroundColor: '#2B75FF',
  borderRadius: 12,
  borderSkipped: 'start',
  barPercentage: 0.4,
  fill: false,
  data: []
}

const WATER_USAGE_ENTRY: UsageDataDatasetEntry = {
  label: 'Vedenkulutus',
  backgroundColor: '#2B75FF',
  borderRadius: 12,
  borderSkipped: 'start',
  barPercentage: 0.4,
  data: []
}

/* Exports */
export const MeterUtils = {
  groupTemperatureHistoryGraphData,
  groupWaterHistoryGraphData
}

/* Utility Functions */

function groupTemperatureHistoryGraphData(data: MeterHistoryValue[]): UsageData {
  const usageData: UsageData = {
    labels: [],
    datasets: [{
      ...TEMPERATURE_USAGE_ENTRY,
      data: []
    }]
  }

  const format = 'DD.MM.YYYY HH:mm'

  const labelsTimestamps: number[] = []
  const dataSetData: number[] = []

  data.forEach((entry: MeterHistoryValue) => {
    if (entry.temperature == null) return
    labelsTimestamps.push(entry.time)
    dataSetData.push(entry.temperature)
  })

  const sortedLabels = labelsTimestamps.splice(0)
    .sort((a, b) => a - b)
    .map(timestamp => moment.unix(timestamp).format(format))

  usageData.labels = sortedLabels
  usageData.datasets[0].data = dataSetData
  return usageData
}

function groupWaterHistoryGraphData(data: MeterHistoryValue[], scale: string): UsageData {
  const format = getDateFormatByScale(scale)
  const groupedByTime = groupBy(data, function(item) {
    return moment.unix(item.time).format(format)
  })

  const usageData: UsageData = {
    labels: [],
    datasets: [
      {
        ...WATER_USAGE_ENTRY,
        data: []
      }
    ]
  }

  if (data.length === 0) {
    return usageData
  }

  // this is the last value before the time period
  const firstKey: string | undefined = groupedByTime.keys().next().value

  const usages: number[] = firstKey != null
    ? (
        groupedByTime.get(firstKey)
          ?.map(i => i.waterUsed)
          .filter((waterUsed): waterUsed is number => waterUsed != null) ?? []
      )
    : []

  let prevMax = usages.length > 0 ? Math.max(...usages) : 0

  if (firstKey != null) {
    groupedByTime.delete(firstKey)
  }

  const dataSetData: number[] = []
  const labels: string[] = []
  groupedByTime.forEach((value, key): void => {
    labels.push(key)

    const usages: number[] = value
      .map(i => i.waterUsed)
      .filter((wu): wu is number => wu !== null && wu !== undefined)

    const currentMax = Math.max(...usages)

    const usageInTimeFrame = currentMax - prevMax
    prevMax = currentMax

    dataSetData.push(litersToCube(usageInTimeFrame))
  })

  usageData.labels = labels
  usageData.datasets[0].data = dataSetData
  return usageData
}

/* Helper Functions */

function getDateFormatByScale(scale: string): string {
  switch (scale) {
    case 'single':
      return 'DD.MM.YYYY HH:mm'

    case 'day':
      return 'DD.MM.YYYY'

    case 'week':
      return 'WW/YYYY'

    case 'month':
      return 'MM.YYYY'

    default:
      return 'DD.MM.YYYY'
  }
}

function litersToCube(input: number): number {
  // Get rid of the last decimal. So that the accuracy is 10s liters. ie 1.413m^3 -> 1.410m^3 as the most accurate meters have 10 liter pulses.
  return Math.round(input / 10) / 100
}
